/**
 * Normalized subtitle layer model (0–1 coordinates relative to the export frame).
 * Center-based: x/y = box center; width/height = fraction of frame dimensions.
 */

import { parseAspectRatio } from '@/lib/videoExportFraming';
import { normalizeRotationSigned } from '@/lib/subtitles/subtitleTransform';

export const SUBTITLE_LAYER_DEFAULTS = {
  x: 0.5,
  y: 0.82,
  width: 0.86,
  height: 0.14,
  rotation: 0,
  textAlign: 'center',
};

export const SUBTITLE_MIN_LAYER = {
  width: 0.18,
  height: 0.06,
};

/** Safe-area insets for on-screen guides only (not enforced while dragging). */
export const SUBTITLE_SAFE_INSETS = {
  '16:9': { top: 0.04, bottom: 0.06, left: 0.04, right: 0.04 },
  '21:9': { top: 0.05, bottom: 0.07, left: 0.06, right: 0.06 },
  '9:16': { top: 0.06, bottom: 0.08, left: 0.06, right: 0.06 },
  '4:5': { top: 0.05, bottom: 0.08, left: 0.05, right: 0.05 },
  '1:1': { top: 0.05, bottom: 0.07, left: 0.05, right: 0.05 },
  '4:3': { top: 0.04, bottom: 0.06, left: 0.04, right: 0.04 },
  '3:4': { top: 0.05, bottom: 0.07, left: 0.05, right: 0.05 },
  '2:3': { top: 0.05, bottom: 0.08, left: 0.06, right: 0.06 },
};

/** Previous default layer widths — migrate to aspect max on load. */
const LEGACY_SUBTITLE_LAYER_WIDTHS = new Set([0.82, 0.84, 0.86]);

function clampNum(n, lo, hi, fb) {
  const v = Number(n);
  return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fb;
}

export function getSubtitleSafeInsets(aspectRatio) {
  const key = String(aspectRatio || '16:9').trim();
  if (SUBTITLE_SAFE_INSETS[key]) return SUBTITLE_SAFE_INSETS[key];
  const ar = parseAspectRatio(key);
  if (ar < 0.65) return SUBTITLE_SAFE_INSETS['9:16'];
  if (ar < 0.9) return SUBTITLE_SAFE_INSETS['4:5'];
  if (ar > 2.1) return SUBTITLE_SAFE_INSETS['21:9'];
  return SUBTITLE_SAFE_INSETS['16:9'];
}

/** Max subtitle box width (normalized) inside the safe-area guide for this aspect ratio. */
export function maxSubtitleLayerWidth(aspectRatio) {
  const safe = getSubtitleSafeInsets(aspectRatio);
  return Math.max(
    SUBTITLE_MIN_LAYER.width,
    Math.min(0.98, 1 - safe.left - safe.right),
  );
}

function defaultWidthForAspect(aspectRatio) {
  return maxSubtitleLayerWidth(aspectRatio);
}

/** Center the layer and expand width to the aspect-ratio maximum. */
export function stretchSubtitleLayerToAspectMax(layer, aspectRatio) {
  const maxW = maxSubtitleLayerWidth(aspectRatio);
  const base = normalizeSubtitleLayer(layer, aspectRatio);
  return {
    ...base,
    width: maxW,
    x: 0.5,
  };
}

function defaultHeightForAspect(aspectRatio) {
  const ar = parseAspectRatio(aspectRatio);
  if (ar < 0.65) return 0.16;
  return 0.14;
}

/** Bottom-anchored legacy style → center-based layer. */
export function subtitleLayerFromLegacyAnchors(anchorX, anchorY, aspectRatio) {
  const ax = clampNum(anchorX, 0.02, 0.98, 0.5);
  const ay = clampNum(anchorY, 0.02, 0.98, 0.88);
  const h = defaultHeightForAspect(aspectRatio);
  return {
    ...SUBTITLE_LAYER_DEFAULTS,
    x: ax,
    y: ay - h / 2,
    width: defaultWidthForAspect(aspectRatio),
    height: h,
    rotation: 0,
    textAlign: 'center',
  };
}

/** Layer center → export anchor (bottom-center point for ASS \\an2). */
export function subtitleLayerToExportAnchors(layer) {
  const ly = clampNum(layer?.y, 0, 1, 0.82);
  const lh = clampNum(layer?.height, 0.04, 0.5, 0.14);
  return {
    anchorX: clampNum(layer?.x, 0.02, 0.98, 0.5),
    anchorY: clampNum(ly + lh / 2, 0.04, 0.98, 0.88),
  };
}

export function normalizeSubtitleLayer(raw, aspectRatio = '16:9') {
  const d = {
    ...SUBTITLE_LAYER_DEFAULTS,
    width: defaultWidthForAspect(aspectRatio),
    height: defaultHeightForAspect(aspectRatio),
  };
  if (!raw || typeof raw !== 'object') return { ...d };

  const align = String(raw.textAlign ?? raw.text_align ?? d.textAlign).toLowerCase();
  const textAlign = align === 'left' || align === 'right' ? align : 'center';
  const maxW = maxSubtitleLayerWidth(aspectRatio);
  let width = clampNum(raw.width, SUBTITLE_MIN_LAYER.width, maxW, d.width);
  if (raw.width != null) {
    const legacyW = Math.round(Number(raw.width) * 100) / 100;
    if (LEGACY_SUBTITLE_LAYER_WIDTHS.has(legacyW)) {
      width = maxW;
    }
  }

  return {
    x: clampNum(raw.x, 0.02, 0.98, d.x),
    y: clampNum(raw.y, 0.02, 0.98, d.y),
    width,
    height: clampNum(raw.height, SUBTITLE_MIN_LAYER.height, 0.45, d.height),
    rotation: normalizeRotationSigned(raw.rotation ?? 0),
    textAlign,
  };
}

/** Clamp layer box inside safe area; returns adjusted layer. */
export function clampSubtitleLayerToSafeArea(layer, aspectRatio) {
  const safe = getSubtitleSafeInsets(aspectRatio);
  const minX = safe.left + layer.width / 2;
  const maxX = 1 - safe.right - layer.width / 2;
  const minY = safe.top + layer.height / 2;
  const maxY = 1 - safe.bottom - layer.height / 2;
  return {
    ...layer,
    x: clampNum(layer.x, minX, maxX, layer.x),
    y: clampNum(layer.y, minY, maxY, layer.y),
  };
}

export function mergeSubtitleLayer(styleLayer, cueLayer, aspectRatio) {
  const base = normalizeSubtitleLayer(styleLayer, aspectRatio);
  if (!cueLayer || typeof cueLayer !== 'object') return base;
  return normalizeSubtitleLayer({ ...base, ...cueLayer }, aspectRatio);
}
